import React, { useEffect, useState } from 'react'
import { Button } from '@mui/material'
import { Icon } from '@iconify/react'
import { Card } from 'primereact/card'
import { Dialog } from 'primereact/dialog'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { CCol, CForm, CFormInput, CFormLabel, CInputGroup, CInputGroupText, CRow } from '@coreui/react'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useUser } from '../contexts/UserContext'
import { useAuth } from '../contexts/AuthContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { useCompanySetting } from '../contexts/CompanySettingContext'
import CInput from './CInput'
import UpdatePin from './UpdatePin'
import UpdatePassword from './UpdatePassword'
import UserGroupSelection from './UserGroupSelection'
import RestaurantSelection from './RestaurantSelection'
import { useUserGroup } from '../contexts/UserGroupContext'

function AddUser({ id, editUserId, visible, setVisible, setEditUserId, editMode, setEditMode, fetchUsers, credential, setCredential, canCreate, canUpdate, canDelete, canVisibleHourlyRate }) {
    const { createUser, getSingleUser, updateUser, deleteUser, archiveUser, updatePin, updatePassword } = useUser()
    const { getSingleUserGroups } = useUserGroup();
    const { toast, auth } = useAuth()
    const { currency } = useCompanySetting()
    const { selectedRestaurantId } = useRestaurant();

    const [changePinVisible, setChangePinVisible] = useState(false)
    const [validated, setValidated] = useState(false);
    const [pinValidated, setPinValidated] = useState(false);
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [changePinCredential, setChangePinCredential] = useState({
        newPin: "",
        confirmNewPin: ""
    })
    const [changePasswordVisible, setChangePasswordVisible] = useState(false)
    const [changePasswordCredential, setChangePasswordCredential] = useState({
        newPassword: "",
        confirmNewPassword: ""
    })

    const handleClose = () => {
        setVisible(false);
        setEditMode(false);
        setEditUserId(null);
        setCredential({
            user_name: "",
            first_name: "",
            last_name: "",
            email: "",
            password: "",
            user_group_id: "",
            telephone: "",
            pin: "",
            access: "",
            GSM: "",
            street: "",
            street_number: "",
            city: "",
            zip: "",
            floor: "",
            table: "",
            role: "",
            is_super_admin: 0,
            rate_per_hour: "",
            image: "",
            restaurantIds: []
        });
    };

    useEffect(() => {
        if (credential?.user_group_id?.value) fetchSingleUserGroup(credential?.user_group_id?.value);
    }, [credential?.user_group_id?.value]);

    const fetchSingleUserGroup = async (groupId) => {
        try {
            const userGroup = await getSingleUserGroups(groupId);
            if (userGroup) {
                setCredential({
                    ...credential,
                    role: userGroup.role,
                    rate_per_hour: userGroup.role === 'staff' ? credential.rate_per_hour : null,
                });
            }
        } catch (error) {
            console.error('Error fetching single user group', error);
        }
    };

    const fetchSingleUser = async () => {
        try {
            const user = await getSingleUser(id);
            if (user) {
                setCredential(user);
            }
        } catch (error) {
            console.error('Error fetching single user', error);
        }
    };

    useEffect(() => {
        if (visible) {
            setCredential({ ...credential, restaurantIds: [...credential.restaurantIds, selectedRestaurantId] })
        }
    }, [visible]);

    useEffect(() => {
        if (editMode && editUserId) fetchSingleUser();
    }, [editMode, editUserId]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCredential({ ...credential, image: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete('delete');

            if (confirmed) {
                await deleteUser(id);
                handleClose()
                fetchUsers()
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    };

    const handleArchive = async () => {
        try {
            const confirmed = await confirmDelete('archive');

            if (confirmed) {
                await archiveUser(id);
                handleClose()
                fetchUsers()
            }
        } catch (error) {
            console.error('Error during archive operation', error);
        }
    };

    const confirmDelete = async (label) => {
        return new Promise((resolve) => {
            confirmDialog({
                message: `Are you sure you want to ${label} this user?`,
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);

        if (form.checkValidity() === false) {
            e.stopPropagation();
            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                const selectedUserGroup = credential.user_group_id.value ? credential.user_group_id.value : null
                const selectedRestaurantIds = credential.restaurantIds.length > 0 ? credential.restaurantIds.map(restaurantId => restaurantId.value) : []

                const hasRestaurants = selectedRestaurantIds.length > 0;
                const isSuperAdmin = credential.is_super_admin === 1;
                const hasUserGroup = Boolean(selectedUserGroup);

                if (visible && hasRestaurants && hasUserGroup) {
                    const data = await createUser({ ...credential, user_group_id: selectedUserGroup, restaurantIds: selectedRestaurantIds })
                    if (!data.error && data.user) {
                        handleClose()
                        fetchUsers(data.user)
                    }
                } else if (editMode && editUserId && (hasRestaurants || isSuperAdmin) && (hasUserGroup || isSuperAdmin)) {
                    const data = await updateUser(id, { ...credential, user_group_id: selectedUserGroup, restaurantIds: selectedRestaurantIds })
                    if (data && !data.error) {
                        handleClose()
                        fetchUsers()
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    const handleUpdatePin = async (e) => {
        e.preventDefault()
        const form = e.currentTarget;

        if (changePinVisible) {
            setPinValidated(true)
        } else {
            setPasswordValidated(true)
        }

        if (form.checkValidity() === false) {
            e.stopPropagation();
            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                if (changePinVisible) {
                    if (changePinCredential.newPin !== changePinCredential.confirmNewPin) {
                        toast.current.show({ severity: 'error', summary: 'User', detail: "New Pin and Confirm New Pin must be same", life: 3000 })
                    } else {
                        const data = await updatePin(id, changePinCredential.newPin)
                        if (!data.error) {
                            handleClosePinPopPop()
                        }
                    }
                }
                if (changePasswordVisible) {
                    if (changePasswordCredential.newPassword !== changePasswordCredential.confirmNewPassword) {
                        toast.current.show({ severity: 'error', summary: 'User', detail: "New Password and Confirm New Password must be same", life: 3000 })
                    } else {
                        const data = await updatePassword(id, changePasswordCredential.newPassword)
                        if (!data.error) {
                            handleClosePinPopPop()
                        }
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    const handleClosePinPopPop = () => {
        if (changePinVisible) {
            setChangePinVisible(false)
            setChangePinCredential({
                newPin: "",
                confirmNewPin: ""
            })
            setPinValidated(false)
            setPasswordValidated(false)
        }
        if (changePasswordVisible) {
            setChangePasswordVisible(false)
            setChangePasswordCredential({
                newPassword: "",
                confirmNewPassword: ""
            })
            setPasswordValidated(false)
            setPinValidated(false)
        }
    }

    return (
        <div>
            <ConfirmDialog />
            <Dialog draggable={false} header={`${changePinVisible ? 'Change PIN' : 'Change Password'}`} visible={changePinVisible || changePasswordVisible} style={{ width: '20vw' }} onHide={handleClosePinPopPop}>
                <CForm onSubmit={handleUpdatePin} noValidate validated={changePinVisible ? pinValidated : passwordValidated}>
                    {changePinVisible &&
                        <UpdatePin
                            changePinCredential={changePinCredential}
                            setChangePinCredential={setChangePinCredential}
                        />
                    }
                    {changePasswordVisible &&
                        <UpdatePassword
                            changePasswordCredential={changePasswordCredential}
                            setChangePasswordCredential={setChangePasswordCredential}
                        />
                    }
                    <div className={`d-flex justify-content-end mt-4 button-container`}>
                        <div className='d-flex'>
                            <Button onClick={handleClosePinPopPop} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            <Button fullWidth variant="contained" color='primary' type="submit" className='ms-3'>
                                Submit
                            </Button>
                        </div>
                    </div>
                </CForm>
            </Dialog>
            <Dialog draggable={false} header={`${visible ? 'Create User' : `Edit User #${id}`}`} visible={visible || (editMode && editUserId !== null)} style={{ width: '40vw' }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container mt-2'>
                        <CRow className='mb-4'>
                            <CCol lg={6}>
                                <CInput
                                    label={'User Name'}
                                    type={'text'}
                                    value={credential.user_name}
                                    onChange={(value) => setCredential({ ...credential, user_name: value })}
                                    required={true}
                                    errorMessage='User name is required.'
                                />
                            </CCol>
                            {!credential?.is_super_admin &&
                                <CCol lg={6}>
                                    <UserGroupSelection
                                        value={credential.user_group_id}
                                        onChange={(value) => setCredential({ ...credential, user_group_id: value })}
                                        multiSelect={false}
                                        required={true}
                                        error={validated && !credential.user_group_id.value}
                                    />
                                </CCol>
                            }
                        </CRow>
                        <CRow className="mb-3">
                            <CCol lg={6}>
                                {visible ?
                                    (
                                        <div>
                                            <div>
                                                <CFormLabel>PIN <span className="text-danger">*</span></CFormLabel>
                                            </div>
                                            <div className='w-100'>
                                                <CFormInput
                                                    type='password'
                                                    pattern='\d*'
                                                    minLength={4}
                                                    maxLength={4}
                                                    value={credential.pin}
                                                    onChange={(e) => setCredential({ ...credential, pin: e.target.value })}
                                                    required
                                                    feedbackInvalid='Please enter a PIN with 4 digit.'
                                                    className='is_not_validated'
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <CFormLabel>PIN</CFormLabel>
                                            <Button fullWidth variant="contained" color="inherit" onClick={() => setChangePinVisible(true)} disabled={!canUpdate} >
                                                Change PIN
                                            </Button>
                                        </>
                                    )
                                }
                            </CCol>
                            <CCol lg={6}>
                                {visible ?
                                    (
                                        <div>
                                            <div>
                                                <CFormLabel>Password {(credential.role === 'admin' || credential.restaurantIds.length > 1) && (<span className="text-danger">*</span>)}</CFormLabel>
                                            </div>
                                            <div className='w-100'>
                                                <CFormInput
                                                    type='password'
                                                    minLength={8}
                                                    value={credential.password}
                                                    onChange={(e) => setCredential({ ...credential, password: e.target.value })}
                                                    required={credential.role === 'admin' || credential.restaurantIds.length > 1}
                                                    feedbackInvalid='Please enter a password with 8 or more characters.'
                                                    disabled={credential.role === 'staff'}
                                                    className='is_not_validated'
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <CFormLabel>Password</CFormLabel>
                                            <Button fullWidth variant="contained" color="inherit" onClick={() => setChangePasswordVisible(true)} disabled={credential.role === 'staff' || !canUpdate}>
                                                Change Password
                                            </Button>
                                        </>
                                    )
                                }
                            </CCol>
                        </CRow>
                        <CRow className="mb-3">
                            <CCol lg={6}>
                                <CInput
                                    label={'First Name'}
                                    type={'text'}
                                    value={credential.first_name}
                                    onChange={(value) => setCredential({ ...credential, first_name: value })}
                                />
                            </CCol>
                            <CCol lg={6}>
                                <CInput
                                    label={'Last Name'}
                                    type={'text'}
                                    value={credential.last_name}
                                    onChange={(value) => setCredential({ ...credential, last_name: value })}
                                />
                            </CCol>
                        </CRow>
                        <CRow className="mb-3">
                            <CCol lg={6}>
                                <CInput
                                    label={'Email'}
                                    type={'email'}
                                    value={credential.email}
                                    required={credential.role === 'admin' ? true : false}
                                    onChange={(value) => setCredential({ ...credential, email: value })}
                                    errorMessage='Please write valid email.'
                                />
                            </CCol>
                            <CCol lg={6}>
                                <div>
                                    <CFormLabel>Number</CFormLabel>
                                    <CFormInput
                                        type={'text'}
                                        maxlength="13"
                                        minlength="10"
                                        value={credential.telephone}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            const numericValue = inputValue.replace(/[^\d+]/g, '');
                                            setCredential({ ...credential, telephone: numericValue });
                                        }}
                                        feedbackInvalid='Enter valid phone number'
                                    />
                                </div>
                            </CCol>
                        </CRow>

                        {
                            !credential.is_super_admin && (
                                <>
                                    <CRow className="mb-3">
                                        {
                                            (credential.role === 'staff' && canVisibleHourlyRate) && (
                                                <CCol lg={6}>
                                                    <div>
                                                        <CFormLabel className="form-label w-50">
                                                            Rate Per Hour <span className="text-danger">*</span>
                                                        </CFormLabel>
                                                        <CInputGroup>
                                                            <CInputGroupText id="basic-addon1">
                                                                {currency}
                                                            </CInputGroupText>
                                                            <CFormInput
                                                                type="number"
                                                                step="any"
                                                                value={credential.rate_per_hour}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.value;
                                                                    if (newValue >= 0) {
                                                                        setCredential({ ...credential, rate_per_hour: newValue })
                                                                    }
                                                                }}
                                                                required
                                                                feedbackInvalid='Rate per hour is required.'
                                                            />
                                                        </CInputGroup>
                                                    </div>
                                                </CCol>
                                            )
                                        }
                                        {
                                            credential.role === 'admin' && (
                                                <>
                                                    <CCol lg={6}>
                                                        <RestaurantSelection
                                                            className="d-flex flex-column"
                                                            labelClassName="me-2"
                                                            multiSelect={true}
                                                            isUserMode={true}
                                                            required={true}
                                                            propsRestaurantId={credential.restaurantIds}
                                                            setPropsRestaurantId={(value) => {
                                                                const updatedCredential = { ...credential, restaurantIds: value };
                                                                setCredential(updatedCredential);
                                                            }}
                                                            error={validated && credential.restaurantIds.length === 0}
                                                        />
                                                    </CCol>
                                                </>
                                            )
                                        }
                                    </CRow>
                                </>
                            )
                        }
                        <CRow className="mb-3">
                            <CFormLabel>Image</CFormLabel>
                            <CCol style={{ position: 'relative' }}>
                                <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="fileInput" />
                                <label htmlFor="fileInput">
                                    <Card
                                        className="create-card"
                                        header={credential.image ? <LazyLoadImage src={credential.image || 'defaultImageURL'} alt="User" className='create-img' /> :
                                            <Icon icon="mdi:user" style={{ color: 'black' }} height={100} width={100} />}
                                    />
                                </label>
                                {credential.image &&
                                    <Icon
                                        icon="mdi:close"
                                        className='close-btn'
                                        height={15}
                                        width={15}
                                        onClick={() => setCredential({ ...credential, image: '' })}
                                    />
                                }
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`d-flex button-container ${(!credential?.is_super_admin && editMode && editUserId && editUserId !== auth.user?.id && canDelete) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (!credential?.is_super_admin && editMode && editUserId && editUserId !== auth.user?.id && canDelete) && (
                                <div>
                                    <Button color='error' variant="contained" onClick={handleDelete} >
                                        Delete
                                    </Button>
                                    {/* <Button color='error' variant="contained" className='ms-3' onClick={handleArchive} >
                                        Archive
                                    </Button> */}
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </div>
    )
}

export default AddUser